import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, MenuIcon, Spacing, Stack, useBreakpoints } from 'ui';
import { PageHeaderTitle } from '@components/PageHeaderTitle';
import { RoleLevel } from '@context/UserOrgCase.types';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { PATHS } from '@utils/paths';
import { UserRole } from 'services/ClaimscoreApiService/shared/types';
import { AddOptionsButton } from './Buttons/AddOptionsButton';
import { MyAccountButton } from './Buttons/MyAccountButton';
import { StyledNavbar } from './MainTopNavbar.styles';

const SIDE_MENU_BTN_LABEL = 'Open Side Navigation Drawer';

interface MainTopNavbarProps {
  readonly toggleSidebar?: () => void;
}

export function MainTopNavbar({ toggleSidebar }: MainTopNavbarProps) {
  const { isMdUp } = useBreakpoints();
  const { pathname } = useLocation();
  const { getUserRole } = useUserOrgCase();
  const isAdminInCurrentOrgOrCase = useMemo(() => {
    const organizationRole =
      getUserRole(RoleLevel.organization) || UserRole.Viewer;
    const caseRole = getUserRole(RoleLevel.case) || UserRole.Viewer;
    const AdminRoles = [UserRole.Admin, UserRole.Owner];

    return (
      AdminRoles.includes(organizationRole) || AdminRoles.includes(caseRole)
    );
  }, [getUserRole]);

  const hasSideBar = useMemo(
    () => isMdUp && pathname !== PATHS.PRIVATE_BASE.root,
    [isMdUp, pathname],
  );

  const shouldShowSideMenuButton = useMemo(
    () => !isMdUp && pathname !== PATHS.PRIVATE_BASE.root,
    [isMdUp, pathname],
  );

  const getLeftToolbar = () => (
    <>
      {shouldShowSideMenuButton ? (
        <Button
          aria-label={SIDE_MENU_BTN_LABEL}
          color="neutral"
          mr={Spacing.Small}
          onClick={toggleSidebar}
          startIcon={
            <MenuIcon
              aria-label={SIDE_MENU_BTN_LABEL}
              title={SIDE_MENU_BTN_LABEL}
            />
          }
          variant="text"
        />
      ) : null}
      <PageHeaderTitle />
    </>
  );

  const getRightToolbar = () => (
    <Stack direction="row" spacing={Spacing.Small}>
      {isAdminInCurrentOrgOrCase ? <AddOptionsButton /> : null}
      <MyAccountButton />
    </Stack>
  );

  return (
    <StyledNavbar
      hasSidebar={hasSideBar}
      leftToolbar={getLeftToolbar()}
      logoVariant="none"
      rightToolbar={getRightToolbar()}
    />
  );
}
